/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type AnimationEvent,
	type ReactNode,
} from 'react';

import { jsx, cssMap, keyframes } from '@compiled/react';

import ExitingPersistence, { useExitingPersistence } from '@atlaskit/motion/exiting-persistence';
import { token } from '@atlaskit/tokens';

import type { ThinkingStatus } from '../../../pm-plugins/thinking-plugin';

export type VisibleThinkingStatus = Exclude<ThinkingStatus, 'idle'>;

type HidePhase = 'entering' | 'visible' | 'exiting' | 'hidden';

/**
 * Drives the outer enter / visible / exit / hidden lifecycle for a surface
 * that needs to fade out before being collapsed to `display: none` — and back
 * in when un-hidden — without unmounting its children (so their internal
 * state survives the hide cycle).
 *
 * `onAnimationEnd` re-reads the live `isHidden` so hide-cancel races (hide
 * then unhide before the exit animation completes) self-correct.
 */
function useHideTransition(isHidden: boolean): {
	onAnimationEnd: () => void;
	phase: HidePhase;
} {
	const [phase, setPhase] = useState<HidePhase>(isHidden ? 'hidden' : 'visible');

	const phaseRef = useRef(phase);
	phaseRef.current = phase;

	useEffect(() => {
		const current = phaseRef.current;
		if (isHidden) {
			if (current === 'visible' || current === 'entering') {
				setPhase('exiting');
			}
			return;
		}
		if (current === 'hidden') {
			setPhase('entering');
		}
	}, [isHidden]);

	const onAnimationEnd = useCallback(() => {
		if (phase === 'entering') {
			setPhase('visible');
			return;
		}
		if (phase === 'exiting') {
			setPhase(isHidden ? 'hidden' : 'entering');
		}
	}, [phase, isHidden]);

	return { phase, onAnimationEnd };
}

const ON_COMPLETE_DELAY_MS = 3000;

const fadeIn = keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});

const styles = cssMap({
	entering: {
		animationName: fadeIn,
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.inout.bold'),
		animationFillMode: 'forwards',
	},
	exiting: {
		animationName: fadeOut,
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.inout.bold'),
		animationFillMode: 'forwards',
		pointerEvents: 'none',
	},
	reducedMotion: {
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
		},
	},
	hidden: {
		display: 'none',
	},
});

type FadeProps = {
	children: ReactNode;
};

/**
 * Inner status-swap child for `<ExitingPersistence>`. Reads `isExiting` /
 * `onFinish` from context so the parent can sequence exit-then-enter, and
 * applies our own fade keyframes so we don't depend on `<FadeIn>`.
 */
function Fade({ children }: FadeProps): JSX.Element {
	const { isExiting, onFinish } = useExitingPersistence();

	return (
		<div
			css={[styles.reducedMotion, isExiting ? styles.exiting : styles.entering]}
			onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
				if (event.target !== event.currentTarget) {
					return;
				}
				onFinish?.();
			}}
		>
			{children}
		</div>
	);
}

type ThinkingTransitionWrapperProps = {
	children: (thinkingStatusPhase: VisibleThinkingStatus) => ReactNode;
	/**
	 * When `true`, the wrapper plays its exit animation and then collapses to
	 * `display: none` (without unmounting children, so internal state is
	 * preserved). When flipped back to `false`, it plays the enter animation.
	 */
	isHidden: boolean;
	onComplete?: () => void;
	onCompleteDelayMs?: number;
	thinkingStatus: ThinkingStatus | undefined;
};

/**
 * Animated wrapper for the Thinking surface.
 *
 * The wrapper coordinates two independent transitions:
 *
 *  - **Outer hide cycle** (`useHideTransition`): the whole surface fades out
 *    and collapses to `display: none` when `isHidden` flips on, and back in
 *    when it flips off. Children are never unmounted during this cycle, so
 *    their internal state survives.
 *  - **Inner status swap** (`<ExitingPersistence exitThenEnter>` + `<Fade>`):
 *    when `thinkingStatus` changes between `thinking` / `complete` / `idle`
 *    the previous status fades out before the next one fades in. The render-
 *    prop `children` is invoked with the currently-displayed status so the
 *    caller can swap component trees.
 */
export function ThinkingTransitionWrapper({
	thinkingStatus,
	isHidden,
	children,
	onComplete,
	onCompleteDelayMs = ON_COMPLETE_DELAY_MS,
}: ThinkingTransitionWrapperProps): JSX.Element {
	useEffect(() => {
		if (thinkingStatus !== 'complete') {
			return;
		}

		const timeoutId = setTimeout(() => {
			onComplete?.();
		}, onCompleteDelayMs);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [onComplete, thinkingStatus, onCompleteDelayMs]);

	const { phase, onAnimationEnd } = useHideTransition(isHidden);

	const handleOuterAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
		// Inner `<Fade>` animations bubble up through this div — only progress
		// the hide state machine for the outer container's own animation.
		if (event.target !== event.currentTarget) {
			return;
		}
		onAnimationEnd();
	};

	return (
		<div
			css={[
				styles.reducedMotion,
				phase === 'entering' && styles.entering,
				phase === 'exiting' && styles.exiting,
				phase === 'hidden' && styles.hidden,
			]}
			onAnimationEnd={handleOuterAnimationEnd}
		>
			<ExitingPersistence exitThenEnter>
				{thinkingStatus === 'thinking' && <Fade key="thinking">{children('thinking')}</Fade>}
				{thinkingStatus === 'complete' && <Fade key="complete">{children('complete')}</Fade>}
			</ExitingPersistence>
		</div>
	);
}
